import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchUsers, fetchUsersWithFilters } from '../services/api';

// Old endpoint - kept for backward compatibility
export const getUsers = createAsyncThunk(
    'users/getUsers',
    async ({ userType, limit = 10, offset = 0 }) => {
        const response = await fetchUsers(userType, limit, offset);
        return {
            data: response.data.data,
            totalCount: response.data.total_count,
            limit,
            offset,
        };
    }
);

// New endpoint with filters and pagination
export const getUsersWithFilters = createAsyncThunk(
    'users/getUsersWithFilters',
    async (params, { rejectWithValue }) => {
        try {
            const data = await fetchUsersWithFilters(params);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch users/organizations.');
        }
    }
);

export const toggleVerified = createAsyncThunk('users/toggleVerified', async (userType) => {
    const response = await fetchUsers(userType);
    return response.data;
});

export const toggleDeletedStatus = createAsyncThunk('users/toggleDeletedStatus', async (userType) => {
    const response = await fetchUsers(userType);
    return response.data;
});

export const getUserDetails = createAsyncThunk('users/getUserDetails', async ({ userId, userType }) => {
    const response = await fetchUsers(userId, userType);
    return response.data;
});

const userSlice = createSlice({
    name: 'users',
    initialState: {
        users: [],
        userDetails: {},
        status: 'idle',
        error: null,
        pagination: {
            limit: 10,
            offset: 0,
            totalCount: 0,
            page: 1,
            page_size: 20,
            count: 0,
            total_count: 0,
            total_pages: 0,
        },
        filters: {},
    },
    reducers: {
        setFilters: (state, action) => {
            state.filters = action.payload;
        },
        resetFilters: (state) => {
            state.filters = {};
        },
    },
    extraReducers: (builder) => {
        builder
            // Old endpoint handlers
            .addCase(getUsers.fulfilled, (state, action) => {
                state.users = action.payload.data;
                state.status = 'succeeded';
                state.pagination = {
                    limit: action.payload.limit,
                    offset: action.payload.offset,
                    totalCount: action.payload.totalCount,
                };
            })
            .addCase(toggleVerified.fulfilled, (state, action) => {
                state.users = action.payload.data;
                state.status = 'succeeded';
            })
            .addCase(toggleDeletedStatus.fulfilled, (state, action) => {
                state.users = action.payload.data;
                state.status = 'succeeded';
            })
            .addCase(getUserDetails.fulfilled, (state, action) => {
                state.userDetails = action.payload.data;
                state.status = 'succeeded';
            })
            // New endpoint handlers
            .addCase(getUsersWithFilters.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(getUsersWithFilters.fulfilled, (state, action) => {
                state.status = 'succeeded';
                if (action.payload.status === 1 || action.payload.data) {
                    state.users = action.payload.data || [];
                    if (action.payload.pagination) {
                        state.pagination = {
                            ...state.pagination,
                            page: action.payload.pagination.page || 1,
                            page_size: action.payload.pagination.page_size || 20,
                            count: action.payload.pagination.count || 0,
                            total_count: action.payload.pagination.total_count || 0,
                            total_pages: action.payload.pagination.total_pages || 0,
                        };
                    }
                } else {
                    state.users = [];
                    state.error = 'Failed to fetch users/organizations';
                }
            })
            .addCase(getUsersWithFilters.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload || action.error.message;
            });
    },
});

export const { setFilters, resetFilters } = userSlice.actions;
export default userSlice.reducer;
